/** @jsxImportSource solid-js */

import { Component, Show, For, createSignal, createEffect } from 'solid-js';
import { ITab, IViewListProps } from './types';
import { translate } from '@fulcrum/lib';

interface IView {
  readonly id: string;
  readonly name: string;
  readonly thumbnail?: string;
}

interface ISwitchTabProps {
  readonly isShow2DView: boolean;
  readonly isShow3DView: boolean;
  readonly tab: string;
  onSwitch(tab: string): void;
}

const SwitchTab: Component<ISwitchTabProps> = (props) => {
  return (
    <Show when={props.isShow2DView && props.isShow3DView}>
      <div class="switch-tab">
        <button
          class={`button ${props.tab === '3D' ? 'primary' : 'secondary'}`}
          onClick={() => {
            props.onSwitch('3D');
          }}
        >
          {translate('TC.Common.Views3D')}
        </button>
        <button
          class={`button ${props.tab === '2D' ? 'primary' : 'secondary'}`}
          onClick={() => {
            props.onSwitch('2D');
          }}
        >
          {translate('TC.Common.Views2D')}
        </button>
      </div>
    </Show>
  );
};

interface ICheckBoxListProps {
  readonly index: number;
  readonly thumbnail?: string;
  readonly listName: string;
  onSelect(index: number, action: string): void;
  readonly isChecked: boolean;
  readonly hideCheckBox?: boolean;
}

const CheckBoxList: Component<ICheckBoxListProps> = (props) => {
  return (
    <li
      class={`list-item group selectable ${props.isChecked ? 'active' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        props.onSelect(props.index, 'single_selection');
      }}
    >
      <div class="checkradio-group mb-0">
        <div class="input-group checkradio">
          <div class="group-items px-1 text-ellipsis">
            <Show when={!props.hideCheckBox}>
              <div
                class="icon checkbox"
                onClick={(e) => {
                  e.stopPropagation();
                  props.onSelect(props.index, 'multi_selection');
                }}
              >
                <input
                  class="custom-input"
                  name="filter"
                  type="checkbox"
                  checked={props.isChecked}
                />
                <label />
              </div>
            </Show>
            <div class="row-center text-ellipsis">
              <Show when={props.thumbnail}>
                <img class="i16" src={props.thumbnail} alt="" />
              </Show>
              <div class="text-ellipsis" title={props.listName}>
                {props.listName}
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

interface IViewListComponentProps {
  readonly isShow2DView: boolean;
  readonly isShow3DView: boolean;
  readonly isLoading: boolean;
  readonly totCnt: number;
  readonly views3dList: IView[];
  readonly tab: string;
  readonly views2dList: IView[];
  readonly selectedRows: number[];
  readonly tot2dCnt: number;
  onSwitch(tab: string): void;
  onScroll(e: Event): void;
  onSelect(index: number, action: string): void;
  readonly disableMultiSelect?: boolean;
}

const ViewListComponent: Component<IViewListComponentProps> = (props) => {
  const currentViews = (): IView[] =>
    props.tab === '3D' ? props.views3dList : props.views2dList;
  const currentCount = (): number =>
    props.tab === '3D' ? props.totCnt : props.tot2dCnt;

  return (
    <>
      <div class="row-distribute">
        <div>
          <h4>{translate('TC.Views.View.Plural')}</h4>
        </div>
        <SwitchTab
          isShow2DView={props.isShow2DView}
          isShow3DView={props.isShow3DView}
          tab={props.tab}
          onSwitch={props.onSwitch}
        />
      </div>

      <section>
        <div class="search-result bg-white" onScroll={(e) => props.onScroll(e)}>
          <ul class="list">
            <Show when={props.isLoading && currentViews().length === 0}>
              <div class="spinner-container">
                <i class="icon-font tc-icon-spinner-dark" />
              </div>
            </Show>

            <For each={currentViews()}>
              {(view, index) => (
                <CheckBoxList
                  index={index()}
                  thumbnail={view.thumbnail}
                  listName={view.name}
                  onSelect={props.onSelect}
                  isChecked={props.selectedRows.includes(index())}
                  hideCheckBox={props.disableMultiSelect}
                />
              )}
            </For>

            <Show when={props.isLoading && currentViews().length > 0}>
              <li class="list-item group">
                <div class="center" style={{ width: '100%' }}>
                  <i class="icon-font tc-icon-spinner-dark mr-1" />
                </div>
              </li>
            </Show>

            <Show when={!props.isLoading && currentCount() === 0}>
              <div class="empty-container">
                <i class="icon-font tc-icon-views" />
                <p>{translate('TCW.Common.NoSearchFound')}</p>
              </div>
            </Show>
          </ul>
        </div>
      </section>
    </>
  );
};

const ViewListContainer: Component<IViewListProps> = (props) => {
  const [isLoading, _setIsLoading] = createSignal(false);
  const [totCnt, _setTotCnt] = createSignal(0);
  const [tot2dCnt, _setTot2dCnt] = createSignal(0);
  const [views3dList, _setViews3dList] = createSignal<IView[]>([]);
  const [views2dList, _setViews2dList] = createSignal<IView[]>([]);
  const [selectedRows, setSelectedRows] = createSignal<number[]>([]);
  const [currentTab, setCurrentTab] = createSignal('3D');

  // Reset when tab changes
  createEffect(() => {
    if (props.tab !== ITab.View) {
      setSelectedRows([]);
    }
  });

  const onSwitch = (tab: string): void => {
    setCurrentTab(tab);
    setSelectedRows([]);
  };

  const onSelect = (index: number, action: string): void => {
    const currentViews = currentTab() === '3D' ? views3dList() : views2dList();
    const view = currentViews[index];
    if (!view) return;

    let newSelectedRows: number[];

    if (action === 'single_selection') {
      newSelectedRows = props.disableMultiSelect
        ? [index]
        : [...selectedRows(), index];
    } else {
      // multi_selection
      const currentSelected = selectedRows();
      if (currentSelected.includes(index)) {
        newSelectedRows = currentSelected.filter((i) => i !== index);
      } else {
        newSelectedRows = [...currentSelected, index];
      }
    }

    setSelectedRows(newSelectedRows);

    // Create view list from selected items
    const selectedViews = newSelectedRows
      .map((i) => currentViews[i])
      .filter(Boolean);

    props.onFilesSubmit({
      selectedObject: selectedViews,
      type: ITab.View,
      source: 'attachment-picker',
    });
  };

  const onScroll = (_e: Event): void => {
    // Handle infinite scroll if needed
  };

  return (
    <Show when={props.tab === ITab.View && props.isShowView}>
      <ViewListComponent
        isShow2DView={props.isShow2DView}
        isShow3DView={props.isShow3DView}
        isLoading={isLoading()}
        totCnt={totCnt()}
        views3dList={views3dList()}
        tab={currentTab()}
        views2dList={views2dList()}
        selectedRows={selectedRows()}
        tot2dCnt={tot2dCnt()}
        onSwitch={onSwitch}
        onScroll={onScroll}
        onSelect={onSelect}
        disableMultiSelect={props.disableMultiSelect}
      />
    </Show>
  );
};

export default ViewListContainer;
